import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, HStack, Icon, Text, usePrefersReducedMotion, } from '@spothero/ui';
import { motion } from 'framer-motion';
import StorageUtils from '@spothero/utils/storage';
import { StorageKeys, StorageTypes } from 'common/constants/storageKeys';
import { GradientText } from './GradientText';
import IconPriceTag from '@spothero/icons/price-tag';
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionBox = motion(Box);
const CALL_OUT_TEXT = 'Total price includes all required fees';
const ENTRANCE_DELAY = 1.2;
const FINAL_REVEAL_DELAY_MS = 3000;
export function AllInPricingCalloutDesktop({ onAnimationComplete, }) {
    const prefersReducedMotion = usePrefersReducedMotion();
    const hasBeenShown = StorageUtils.get(StorageKeys.ALL_IN_PRICING_CALLOUT_SHOWN, StorageTypes.SESSION) === true;
    const shouldAnimate = useMemo(() => !(prefersReducedMotion || hasBeenShown), [prefersReducedMotion, hasBeenShown]);
    const [textAnimationComplete, setTextAnimationComplete] = useState(() => !shouldAnimate);
    const [showFinalText, setShowFinalText] = useState(() => !shouldAnimate);
    const markAnimationComplete = useCallback(() => {
        if (!hasBeenShown) {
            StorageUtils.set(StorageKeys.ALL_IN_PRICING_CALLOUT_SHOWN, true, StorageTypes.SESSION);
        }
        if (onAnimationComplete) {
            onAnimationComplete();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasBeenShown, onAnimationComplete]);
    useEffect(() => {
        if (!shouldAnimate) {
            markAnimationComplete();
        }
    }, [shouldAnimate, markAnimationComplete]);
    const handleTextAnimationComplete = useCallback(() => {
        if (!shouldAnimate || textAnimationComplete) {
            return;
        }
        setTextAnimationComplete(true);
    }, [shouldAnimate, textAnimationComplete]);
    useEffect(() => {
        if (!textAnimationComplete || showFinalText) {
            return;
        }
        if (!shouldAnimate || typeof window === 'undefined') {
            setShowFinalText(true);
            markAnimationComplete();
            return;
        }
        const timeoutId = window.setTimeout(() => {
            setShowFinalText(true);
            markAnimationComplete();
        }, FINAL_REVEAL_DELAY_MS);
        return () => window.clearTimeout(timeoutId);
    }, [
        textAnimationComplete,
        showFinalText,
        shouldAnimate,
        markAnimationComplete,
    ]);
    const containerSpring = shouldAnimate
        ? {
            type: 'spring',
            duration: 0.6,
            stiffness: 300,
            damping: 20,
            mass: 1,
            delay: ENTRANCE_DELAY,
        }
        : { duration: 0 };
    const textSpringTransition = shouldAnimate
        ? {
            type: 'spring',
            duration: 0.6,
            stiffness: 300,
            damping: 20,
            mass: 1,
            delay: ENTRANCE_DELAY,
        }
        : { duration: 0 };
    return (<Flex data-testid="AllInPricingCallout" flexGrow={1} justifyContent="flex-end">
            <MotionFlex align="center" borderRadius="md" paddingX={4} paddingY={3} initial={{
            x: shouldAnimate ? 120 : 0,
            opacity: shouldAnimate ? 0 : 1,
            backgroundColor: 'black',
        }} animate={{
            x: 0,
            opacity: 1,
            backgroundColor: showFinalText ? 'white' : 'black',
        }} transition={{
            x: containerSpring,
            opacity: containerSpring,
            backgroundColor: {
                duration: shouldAnimate ? 0.3 : 0,
                ease: 'easeInOut',
            },
        }}>
                <HStack spacing={2} align="center">
                    <MotionBox color={shouldAnimate ? '#52A8FB' : '#1271EB'} animate={{ color: '#1271EB' }} transition={{
            duration: shouldAnimate ? 0.6 : 0,
            delay: shouldAnimate ? ENTRANCE_DELAY : 0,
            ease: 'easeInOut',
        }}>
                        <Icon as={IconPriceTag} boxSize={4} marginTop={2}/>
                    </MotionBox>

                    <Box position="relative" minWidth="max-content">
                        <MotionBox fontSize="base" fontWeight="semibold" whiteSpace="nowrap" initial={{ opacity: showFinalText ? 0 : 1 }} animate={{ opacity: showFinalText ? 0 : 1 }} transition={{ duration: 0.2, ease: 'easeInOut' }} sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pointerEvents: 'none',
        }}>
                            <GradientText fontWeight="semibold" animationSpeed={3}>
                                {CALL_OUT_TEXT}
                            </GradientText>
                        </MotionBox>

                        <MotionText fontSize="base" fontWeight="semibold" whiteSpace="nowrap" color={showFinalText ? 'black' : 'white'} initial={{
            y: shouldAnimate ? 12 : 0,
            opacity: shouldAnimate ? 0 : 1,
        }} animate={{
            y: 0,
            opacity: showFinalText ? 1 : 0,
            color: showFinalText ? 'black' : 'white',
        }} transition={{
            y: textSpringTransition,
            opacity: {
                duration: 0.2,
                ease: 'easeInOut',
            },
            color: {
                duration: 0.2,
                ease: 'easeInOut',
            },
        }} onAnimationComplete={handleTextAnimationComplete}>
                            {CALL_OUT_TEXT}
                        </MotionText>
                    </Box>
                </HStack>
            </MotionFlex>
        </Flex>);
}
